import * as m from '@staiter/i18n';
import type { ReactElement } from 'react';
import { cloneElement } from 'react';
import { useAuthStore } from '~/store/auth';

/**
 * UX18 — RBAC visible-but-disabled wrapper.
 *
 * Если у пользователя нет ability — child element рендерится disabled
 * с tooltip объясняющим какой role нужен. Source of approver_hints —
 * `/auth/me` `scope.approver_hints` (B6). Graceful fallback на generic
 * текст если hint отсутствует.
 *
 * Использование:
 *   <DisabledWithReason ability="project:write">
 *     <Button onClick={createProject}>{m.ui_sozdat_proekt()}</Button>
 *   </DisabledWithReason>
 */
export function DisabledWithReason(props: {
  ability: string;
  children: ReactElement<{ disabled?: boolean; title?: string }>;
  /** Allow-condition. Если переопределён — используется вместо store-check. */
  can?: boolean;
}): JSX.Element {
  const userScope = useAuthStore((s) => s.user?.scope);
  const abilities = (userScope?.abilities ?? {}) as Record<string, string[]>;
  const approverHints = (userScope?.approver_hints ?? {}) as Record<string, string>;
  // ability format: 'entity:action' — abilities map keyed by entity, value=actions[].
  const [entity, action] = props.ability.split(':') as [string, string];
  const userActions = abilities[entity] ?? [];
  const can = props.can ?? userActions.includes(action);

  if (can) return props.children;

  const approverRole = approverHints[props.ability];
  const tooltip = approverRole
    ? m.rbac_disabled_tooltip_with_role({ role: roleLabel(approverRole) })
    : m.rbac_disabled_tooltip_generic();

  return cloneElement(props.children, {
    disabled: true,
    title: tooltip,
  });
}

function roleLabel(code: string): string {
  switch (code) {
    case 'admin':
      return m.rbac_role_admin();
    case 'manager':
      return m.rbac_role_manager();
    default:
      return code;
  }
}
